import { useEffect, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';
import { setClinicTimeZone } from '../i18n/clinicClock';
import { setPortalConfig } from '../portal/portalConfigStore';
import { useI18n } from '../i18n/I18nProvider';
import { brandFromApi } from './brand';
import { getPinnedBrandId, setBrand } from './brandStore';
import { useTheme } from './ThemeProvider';

/**
 * Fetches tenant branding and publishes it to the brand store (the app's
 * BrandGate, on the web):
 *  - before sign-in, a pinned brand (`VITE_BRAND_ID` / `?brandId`) loads from
 *    the unauthenticated `/portal/branding` so the login page is already the
 *    clinic's;
 *  - after sign-in, the authenticated `/portal/config` is authoritative and
 *    also carries the clinic's time zone.
 * A network failure keeps the last-known-good brand; only a successful
 * response may change what the patient sees.
 *
 * Both requests declare `surface=portal`. The native app and the web portal
 * hit the same two routes, so the server cannot tell them apart from the
 * request alone; saying which surface is asking is what lets a tenant who has
 * configured a portal-specific palette be served it here while the app keeps
 * the shared one. Tenants without an override are unaffected — the server
 * falls back to the shared palette — and the payload shape is identical
 * either way, so nothing downstream of `brandFromApi` changes.
 */
export function BrandGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { displayName } = useTheme();
  const { t } = useI18n();

  useEffect(() => {
    const brandId = getPinnedBrandId();
    if (!brandId || isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/portal/branding?brandId=${encodeURIComponent(brandId)}&surface=portal`,
        );
        if (!res.ok || cancelled) return;
        const brand = brandFromApi(await res.json(), API_BASE);
        if (brand && !cancelled) setBrand(brand);
      } catch {
        /* keep last-known-good */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
        const res = await fetch(`${API_BASE}/portal/config?surface=portal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const config = await res.json();
        if (cancelled) return;
        setBrand(config?.branding ? brandFromApi(config.branding, API_BASE) : null);
        setClinicTimeZone(config?.timezone);
        // `modules` gates whole features and `telemedPatientSummaryMode` decides
        // whether the visit-summary surface exists at all; both were previously
        // dropped on the floor here.
        setPortalConfig(config);
      } catch {
        /* keep last-known-good */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  useEffect(() => {
    document.title = `${displayName ?? t('app.platformName')} · ${t('app.title')}`;
  }, [displayName, t]);

  return <>{children}</>;
}
